import React, { useCallback, useState } from 'react';

import {
	RemixOverlay as RemixOptionsOverlay,
	type RemixOptionSelectionParameters,
} from './remix-overlay';
import { RemixThinkingBar } from './remix-thinking-bar';

/**
 * Props for the Remix container composed entry point.
 */
interface RemixContainerProps {
	/** The text content currently selected by the user, shown in the remix options overlay. */
	selectedText: string;
	/** Called when the user dismisses the remix flow (e.g. closes the overlay or preview). */
	onClose: () => void;
	/** Optional callback when the user selects a remix type (e.g. chart, diagram, visual aid, database). */
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
	/** Called when the internal thinking state changes */
	onThinkingStateChange?: (isThinking: boolean) => void;
}

/**
 * Composed entry point for the Remix feature.
 *
 * Renders the RemixOptionsOverlay for the user to pick a remix type.
 * Manages its own thinking state and notifies the consumer via onThinkingStateChange.
 */
export function RemixContainer({
	selectedText,
	onClose,
	onOptionSelect,
	onThinkingStateChange,
}: RemixContainerProps): React.ReactElement {
	const [isThinking, setIsThinking] = useState(false);

	const handleOptionSelect = useCallback(
		(params: RemixOptionSelectionParameters) => {
			setIsThinking(true);
			onThinkingStateChange?.(true);
			onOptionSelect?.(params);
		},
		[onOptionSelect, onThinkingStateChange],
	);

	const handleStopThinking = useCallback(() => {
		setIsThinking(false);
		onThinkingStateChange?.(false);
	}, [onThinkingStateChange]);

	if (isThinking) {
		return <RemixThinkingBar onStop={handleStopThinking} />;
	}

	return (
		<RemixOptionsOverlay
			selectedText={selectedText}
			onClose={onClose}
			onOptionSelect={handleOptionSelect}
		/>
	);
}
